import React, { useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext"; // ← path fixed (parent is in Orders/)
import { toast } from "react-toastify";
import {
  Settings, Wallet, Percent, Gift, Ticket, BadgePercent,
  Clock, Printer, Image as ImageIcon, Wrench, Truck
} from "lucide-react";

// ⬇️ everything now comes from ./OrdersSetting/*
import TabsBar from "./OrdersSetting/components/TabsBar";
import { Select } from "./OrdersSetting/components/FormControls";

import {
  DeliveryChargesSection,
  ServicesSection,
  DiscountsSection,
  CouponSection,
  LoyaltySection,
  FreeItemsSection,
  OrderChargesSection,
  OrderTimingSection,
  PrintingSection,
  BannerSection,
  ConfigurationSection,
} from "./OrdersSetting"; // re-exported via index.js

import useOrders from '../Services/OrderServices/useOrders';
import ClientSearchSelect from "../common/ClientSearchSelect";

const TABS = [
  { key: "deliveryCharges", label: "Delivery Charges", icon: Wallet },
  { key: "services", label: "Services", icon: Truck },
  { key: "discounts", label: "Discounts", icon: Percent },
  { key: "coupon", label: "Coupon", icon: Ticket },
  { key: "loyaltyPoints", label: "Loyalty Points", icon: BadgePercent },
  { key: "free", label: "Free Items", icon: Gift },
  { key: "orderCharges", label: "Order Charges", icon: Wallet },
  { key: "orderTiming", label: "Order Timing", icon: Clock },
  { key: "printing", label: "Receipt Printing", icon: Printer },
  { key: "bannercreate", label: "Banner", icon: ImageIcon },
  { key: "config", label: "Configuration", icon: Wrench },
];

export default function AdminOrdersSetting() {

  const { getCatalogueStatus, changeCatalogueStatus } = useOrders();

  const { api, AllClients } = useContext(GlobalContext);
  const [activeTab, setActiveTab] = useState("deliveryCharges");
  const [userID, setUserID] = useState("");
  const [catalogueStatus, setCatalogueStatus] = useState("");

  const clientOptions = useMemo(
    () => (AllClients || []).map((c) => ({
      code: c.us_code,
      name: c.us_name || c.organization_name || c.us_loginid
    })),
    [AllClients]
  );

  useEffect(() => {
    if (!userID && clientOptions.length) setUserID(clientOptions[0].code);
  }, [clientOptions, userID]);

  const loadCatalogue = async () => {
    if (!userID) return;

    try {
      const result = await getCatalogueStatus(userID);

      if (result.success) {
        setCatalogueStatus(result.status);
      } else {
        setCatalogueStatus("");
        // toast.error(result.message);
      }
    } catch {
      setCatalogueStatus("");
    }
  };


  const toggleCatalogue = async () => {
    if (!userID) return;

    const next =
      catalogueStatus === "show"
        ? "hide"
        : "show";

    try {
      // optimistic update
      setCatalogueStatus(next);

      const result =
        await changeCatalogueStatus({
          userid: userID,
          status: next,
        });

      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
        setCatalogueStatus(catalogueStatus); // rollback
      }
    } catch {
      toast.error("Failed to update");
      setCatalogueStatus(catalogueStatus); // rollback
    }
  };


  useEffect(() => { loadCatalogue(); }, [userID]); // eslint-disable-line

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <Settings size={20} />
          </span>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Order Settings</h1>
            <p className="text-sm text-gray-500">Configure delivery, charges, coupons, loyalty, printing and more.</p>
          </div>
        </div>

        <div className="flex w-full items-center gap-3 sm:w-auto">
          <ClientSearchSelect
            clients={clientOptions.map((c) => ({
              us_code: c.code,
              us_name: c.name,
            }))}
            value={userID}
            className="sm:w-64"
            placeholder={
              clientOptions.length === 0 ? "No clients" : "Select Client"
            }
            onChange={(client) => {
              setUserID(client?.us_code || "");
            }}
          />
          {catalogueStatus && (
            <button
              onClick={toggleCatalogue}
              className={
                "rounded-lg px-3 py-2 text-sm font-semibold " +
                (catalogueStatus === "show"
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200")
              }
              title="Toggle order accepting"
            >
              {catalogueStatus === "show" ? "Accepting Orders: ON" : "Accepting Orders: OFF"}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <TabsBar tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {/* Panels */}
      {activeTab === "deliveryCharges" && <DeliveryChargesSection userID={userID} />}
      {activeTab === "services" && <ServicesSection userID={userID} />}
      {activeTab === "discounts" && <DiscountsSection userID={userID} />}
      {activeTab === "coupon" && <CouponSection userID={userID} />}
      {activeTab === "loyaltyPoints" && <LoyaltySection userID={userID} />}
      {activeTab === "free" && <FreeItemsSection userID={userID} />}
      {activeTab === "orderCharges" && <OrderChargesSection userID={userID} />}
      {activeTab === "orderTiming" && <OrderTimingSection userID={userID} />}
      {activeTab === "printing" && <PrintingSection userID={userID} />}
      {activeTab === "bannercreate" && <BannerSection userID={userID} />}
      {activeTab === "config" && <ConfigurationSection userID={userID} />}
    </div>
  );
}
